import React, { useState, useEffect, useMemo } from 'react';
import { Link, Redirect, useLocation } from "react-router-dom";
import { TextField, Button, Snackbar } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import Autocomplete from '@material-ui/lab/Autocomplete';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import TwitterIcon from '@material-ui/icons/Twitter';
import InstagramIcon from '@material-ui/icons/Instagram';
import FacebookIcon from '@material-ui/icons/Facebook';
import neighborhoods from '../asset/neighborhoods.js';
import xtownCity from '../asset/xtown-city.svg';
import '../styles/Manage.css';
import Header from "./Header.js";
import http from '../http.js';

const Input = props => (
    <TextField
        error={ props.error }
        disabled={ props.disabled }
        label= { props.label }
        value={ props.value }
        onChange={ props.onChangeFunc }
        helperText={ props.error ? props.errMsg : props.normalMsg}
        variant="outlined"
        style = {{width: '500px'}}
        margin="normal"
        size="small"
    />
);

function decodeAccountParam(raw) {
    if (!raw) return null;
    try {
        const email = atob(raw).trim().toLowerCase();
        if (!email || !/\S+@\S+\.\S+/.test(email)) return null;
        return email;
    } catch {
        return null;
    }
}

export default function App () {
    const location = useLocation();
    const [name, setName] = useState('');
    const [nameErr, setNameErr] = useState(false);
    const [selectedNeighborhoods, setSelectedNeighborhoods] = useState([]);
    const [selectedNeighborhoodsErr, setSelectedNeighborhoodsErr] = useState(false);
    const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
    const [failureSnackbarOpen, setFailureSnackbarOpen] = useState(false);
    const [unsubscribeUser, setUnsubscribeUser] = useState(false);

    const query = new URLSearchParams(location.search);
    const accountParam = query.get('account');
    const email = useMemo(() => decodeAccountParam(accountParam), [accountParam]);
    const encodedEmail = email ? btoa(email) : '';
    const needsLookup = !email;

    const handleSuccessSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
          return;
        }
        setSuccessSnackbarOpen(false);
    };

    const handleFailureSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
          return;
        }
        setFailureSnackbarOpen(false);
    };

    useEffect(() => {
        if (!email) return;
        setUnsubscribeUser(false);
        async function fetchData() {
            try {
                const result = await http.post('/info', { email });
                if (result.data === 'User not found' || !result.data?.name) {
                    setUnsubscribeUser(true);
                } else {
                    const neighborhoodList = [];
                    setName(result.data.name);
                    (result.data.neighborhoods || []).forEach(d => {
                        const index = neighborhoods.NEIGHBORHOODS.map(nb => nb.id).indexOf(d);
                        if (index >= 0) neighborhoodList.push(neighborhoods.NEIGHBORHOODS[index]);
                    });
                    setSelectedNeighborhoods(neighborhoodList);
                }
            } catch (e) {
                console.log(e);
                setUnsubscribeUser(true);
            }
        }
        fetchData();
    }, [email]);

    if (needsLookup) {
        return <Redirect to="/" />;
    }

    async function handleChange(e) {
        e.preventDefault();
        if (!name.trim() || 
            selectedNeighborhoods.length === 0) {
            if (!name.trim()) setNameErr(true);
            else setNameErr(false);
            if (selectedNeighborhoods.length === 0) setSelectedNeighborhoodsErr(true);
            else setSelectedNeighborhoodsErr(false);
            setFailureSnackbarOpen(true);
        } else {
            setNameErr(false);
            setSelectedNeighborhoodsErr(false);
            setSuccessSnackbarOpen(true);
            
            var data = {
                name: name,
                email: email,
                neighborhoods: selectedNeighborhoods
            }

            const response = http.post('/update', data)
            console.log(response.data);
        }
    }

    if (unsubscribeUser) {
        return (
            <div>
                <Header />
                <div className="Unsubscribe" style={{ textAlign: 'center', maxWidth: 900, margin: '0 auto', padding: '32px 16px' }}>
                    <img src={xtownCity} alt="Logo" style={{ maxWidth: 500, width: '100%', marginBottom: 32 }} />
                    <h1 style={{ fontSize: '2.5rem', fontWeight: 700, marginBottom: 24 }}>
                        Hey! We can't find an active subscription for <b>{email}</b>.
                    </h1>
                    <div style={{ fontSize: '1.15rem', marginBottom: 18 }}>
                        It looks like you’re not currently subscribed to our newsletter. If you’d like to stay up to date with the latest stories and data about your neighborhood, you can easily re-subscribe below.
                    </div>
                    <Button
                        style={{ width: 260, fontSize: '1.1rem', fontWeight: 600, margin: '16px 0', background: '#2196f3' }}
                        className="subscribeButton"
                        variant="contained"
                        color="primary"
                        href="/"
                        disableElevation
                    >
                        SUBSCRIBE TO NEWSLETTER
                    </Button>
                    <div style={{ fontSize: '1.1rem', margin: '32px 0 18px 0' }}>
                        In the meantime, feel free to explore our website or follow us on social media for updates about Los Angeles.
                    </div>
                    <Button
                        style={{ width: 260, fontSize: '1.1rem', fontWeight: 600, margin: '0 0 24px 0' }}
                        className="manageButton"
                        variant="contained"
                        color="primary"
                        href="https://xtown.la/"
                        disableElevation
                    >
                        EXPLORE CROSSTOWN &ensp;<ArrowForwardIcon />
                    </Button>
                    <div className="socialMediaIcons" style={{ marginTop: 12 }}>
                        <TwitterIcon color='primary' fontSize='large' style={{ margin: '0 8px' }} />
                        <InstagramIcon color='primary' fontSize='large' style={{ margin: '0 8px' }} />
                        <FacebookIcon color='primary' fontSize='large' style={{ margin: '0 8px' }} />
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div>
            <Header />
            <div className="left-form">
                <h1> Manage Subscriptions </h1>
                Make sure to click the button below to save your preferences.
                <br></br>
                <form noValidate autoComplete="off">
                    <Input 
                        label="Email Address"
                        value={ email } 
                        errMsg="Please enter a valid email address"
                        normalMsg=""
                        disabled={true}
                    />
                    <br></br>
                    <Input 
                        error={nameErr} 
                        label="First Name" 
                        value={ name } 
                        onChangeFunc={e=>setName(e.target.value)}
                        errMsg="Please enter a first name"
                        normalMsg=""
                        disabled={false}
                    />
                    <Autocomplete
                        multiple
                        options={neighborhoods.NEIGHBORHOODS}
                        getOptionLabel={(option) => option.name}
                        value={ selectedNeighborhoods }
                        onChange={
                            (e, input) => { setSelectedNeighborhoods(input);}
                        }
                        renderInput={(params) => (
                        <TextField
                            {...params}
                            error={selectedNeighborhoodsErr}
                            variant="outlined"
                            label="Neighborhoods"
                            margin="normal"
                            size="small"
                            style = {{width: '500px'}}
                            helperText={selectedNeighborhoodsErr ? "Please enter at least one neighborhood" : ""}
                        />
                        )}
                    />
                    <br></br>
                    <br></br>
                    <div className="options">
                        <Button 
                            style = {{width: '200px'}}
                            className="manageButton"
                            variant="contained" 
                            color="primary"
                            onClick={handleChange}
                            disableElevation
                            >
                            Save My Preferences
                        </Button>
                        <span className="manageText">&ensp; or <Link className="unsubscribeLink" to={`/unsubscribe?account=${encodedEmail}`}>Unsubscribe</Link></span>
                    </div>
                </form>
            </div>
            <Snackbar 
              anchorOrigin={{vertical: 'top', horizontal: 'center'}} 
              open={successSnackbarOpen} 
              autoHideDuration={5000} 
              onClose={handleSuccessSnackbarClose}>
                <Alert onClose={handleSuccessSnackbarClose} severity="success">
                <b>Well done!</b> You successfully saved your changes!
                </Alert>
            </Snackbar>
            <Snackbar 
              anchorOrigin={{vertical: 'top', horizontal: 'center'}} 
              open={failureSnackbarOpen} 
              autoHideDuration={5000} 
              onClose={handleFailureSnackbarClose}>
                <Alert onClose={handleFailureSnackbarClose} severity="error">
                <b>Oh Snap! <u>Change a few things up</u></b> and try submitting again.
                </Alert>
            </Snackbar>
        </div> 
    );
}